import { useState } from 'react';
import { Link } from 'react-router';
import type { AccountSuspensionNotice } from '@apos/shared';
import { BrandMark, Button } from '../../components/ui';
import { request } from '../../lib/api';
import { getNative } from '../../lib/native';
import { AccountDeletionPanel, type AccountDeletionPreview, type AccountDeletionResult } from '../../app/pages/AccountDeletionPanel';
import { ContactLink } from '../../app/pages/LegalPage';
import { useSession } from '../../state/session';
import './moderation.css';

const day = (t: number) => new Date(t).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

/**
 * Shown instead of every signed-in page while the account is suspended service-wide (server 403
 * `account_suspended`, WebSocket close 4423; docs/release/CONTENT_SAFETY.md, "Suspension"). It shows
 * the reason category and dates only, how to appeal by email, sign-out, and account deletion.
 */
export function SuspendedScreen({ notice, onSignOut }: { notice: AccountSuspensionNotice; onSignOut?: () => void | Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const signOut = async () => {
    setBusy(true);
    try {
      await (onSignOut ? onSignOut() : useSession.getState().logout());
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="splash suspended" role="main" data-testid="suspended-screen">
      <div className="splash__mark">
        <BrandMark size={44} />
      </div>
      <h1 className="suspended__title">Account suspended</h1>
      <p className="suspended__body">
        This Airview Studio account is suspended for breaking the community rules
        {notice.until ? <> until <strong data-testid="suspended-until">{day(notice.until)}</strong></> : null}. You cannot sign in, join shoots, share your
        location or use crew radio while it is suspended.
      </p>
      <p className="suspended__reason">
        Reason: <strong data-testid="suspended-reason">{notice.reasonLabel}</strong>
        <span className="muted"> · since {day(notice.since)}</span>
      </p>
      <p className="suspended__body">
        To appeal, or if you think this is a mistake, email <ContactLink contact={notice.support} testId="suspended-support" /> from the address on this account.
      </p>
      <div className="suspended__actions">
        <Button size="sm" icon="logout" onClick={() => void signOut()} disabled={busy} data-testid="suspended-sign-out">
          Sign out
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setDeleting((d) => !d)} aria-expanded={deleting}>
          Delete account
        </Button>
      </div>
      {deleting ? (
        <div className="suspended__delete">
          <AccountDeletionPanel
            preview={() => request<AccountDeletionPreview>('GET', '/account/deletion')}
            deleteAccount={(body) => request<AccountDeletionResult>('POST', '/account/deletion', body)}
            onDeleted={async () => {
              getNative()?.clearAccountData?.();
              await useSession.getState().logout();
              window.location.replace('/login');
            }}
          />
        </div>
      ) : null}
      <nav className="legal-foot" aria-label="Legal">
        <Link to="/support">Support</Link>
        <Link to="/privacy">Privacy policy</Link>
      </nav>
    </div>
  );
}
